import React from 'react';
import {
  VictoryChart,
  VictoryArea,
  VictoryAxis,
  VictoryTheme,
  VictoryTooltip,
  VictoryLegend
} from 'victory';
import { formatPercentageES, formatPeriodES } from '../../../config/reportTranslations';
import './Charts.css';
import { REPORT_CHART_COLORS } from '../../../constants/colors';

const COLORS = REPORT_CHART_COLORS;

/**
 * Gráfico 12: Riesgo de Crédito
 *
 * Area chart showing asset quality indicators (C1, C2, C3)
 */

export interface AssetQualityIndicatorData {
  period: string;
  C1?: number;  // Productive Assets / Total Assets
  C2?: number;  // Non-Performing Loan Ratio (NPL)
  C3?: number;  // Non-Performing Portfolio Coverage
}

export interface Grafico12Props {
  data: AssetQualityIndicatorData[];
  title?: string;
}

const INDICATOR_COLORS = {
  C1: COLORS.secondary,  // Teal
  C2: COLORS.accent,     // Red (NPL - lower is better)
  C3: COLORS.primary,    // Dark gray (Coverage - higher is better)
};

const INDICATOR_NAMES = {
  C1: 'Activo Productivo/Total',
  C2: 'Morosidad',
  C3: 'Cobertura Cartera Improductiva',
};

export const Grafico12: React.FC<Grafico12Props> = ({
  data,
  title = 'Gráfico 12: Riesgo de Crédito'
}) => {
  if (!data || data.length === 0) {
    return (
      <div className="chart-container">
        <h4 className="chart-title">{title}</h4>
        <p className="chart-no-data">No hay datos disponibles para este período</p>
      </div>
    );
  }

  const sortedData = [...data].sort((a, b) => a.period.localeCompare(b.period));

  // Transform data for each indicator
  const indicators = ['C1', 'C2', 'C3'] as const;
  const chartDatasets = indicators.map(indicator => ({
    indicator,
    name: INDICATOR_NAMES[indicator],
    color: INDICATOR_COLORS[indicator],
    data: sortedData
      .filter(d => d[indicator] !== undefined && d[indicator] !== null)
      .map(d => ({
        x: formatPeriodES(d.period),
        y: d[indicator]! * 100,
        // Store metadata for tooltip
        indicatorName: INDICATOR_NAMES[indicator],
        period: formatPeriodES(d.period),
        value: formatPercentageES(d[indicator]! * 100)
      }))
  })).filter(ds => ds.data.length > 0);

  if (chartDatasets.length === 0) {
    return (
      <div className="chart-container">
        <h4 className="chart-title">{title}</h4>
        <p className="chart-no-data">No hay datos de calidad de activos disponibles</p>
      </div>
    );
  }

  // Calculate global y-axis domain
  const allValues = chartDatasets.flatMap(ds => ds.data.map(d => d.y));
  const minValue = Math.min(...allValues);
  const maxValue = Math.max(...allValues);
  const padding = (maxValue - minValue) * 0.1;
  const yDomain: [number, number] = [
    Math.max(0, minValue - padding),
    maxValue + padding
  ];

  return (
    <div className="chart-container">
      <h4 className="chart-title">{title}</h4>

      <div className="chart-wrapper">
        <VictoryChart
          theme={VictoryTheme.material}
          domainPadding={{ x: 20 }}
          padding={{ top: 40, bottom: 60, left: 60, right: 20 }}
        >
          {/* Legend */}
          <VictoryLegend
            x={70}
            y={0}
            orientation="horizontal"
            gutter={15}
            style={{ labels: { fontSize: 9 } }}
            data={chartDatasets.map(ds => ({
              name: ds.name,
              symbol: { fill: ds.color }
            }))}
          />

          {/* X-Axis */}
          <VictoryAxis
            style={{
              axis: { stroke: '#666' },
              tickLabels: {
                fontSize: 9,
                padding: 5,
                angle: -45,
                textAnchor: 'end'
              },
              grid: { stroke: '#e0e0e0', strokeDasharray: '3,3' }
            }}
          />

          {/* Y-Axis */}
          <VictoryAxis
            dependentAxis
            domain={yDomain}
            tickFormat={(t: number) => `${t.toFixed(0)}%`}
            style={{
              axis: { stroke: '#666' },
              tickLabels: { fontSize: 9, padding: 5 },
              grid: { stroke: '#e0e0e0', strokeDasharray: '3,3' }
            }}
          />

          {/* Area charts for each indicator */}
          {chartDatasets.map(ds => (
            <VictoryArea
              key={ds.indicator}
              data={ds.data}
              style={{
                data: {
                  fill: ds.color,
                  fillOpacity: 0.3,
                  stroke: ds.color,
                  strokeWidth: 2
                }
              }}
              labels={({ datum }) => `${datum.indicatorName}\n${datum.period}: ${datum.value}`}
              labelComponent={
                <VictoryTooltip
                  style={{ fontSize: 9 }}
                  flyoutStyle={{
                    fill: 'white',
                    stroke: ds.color,
                    strokeWidth: 1
                  }}
                  cornerRadius={4}
                  pointerLength={6}
                />
              }
            />
          ))}
        </VictoryChart>
      </div>

      <p className="chart-description">
        Los indicadores de calidad de activos evalúan la salud del portafolio de crédito.
        C1 y C3 mayores son positivos; C2 (morosidad) menor es mejor.
      </p>

      <p className="chart-source">
        Fuente: Estados financieros de la institución
      </p>
    </div>
  );
};

export default Grafico12;
